import type { PromoOffer } from '@/mocks/promos';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const CONDITIONS: Record<number, { icon: string; text: string }[]> = {
  1: [
    { icon: 'ri-capsule-line', text: 'Распространяется на все витамины и БАД в каталоге' },
    { icon: 'ri-shopping-bag-line', text: 'Минимальная сумма заказа — 500 ₽' },
    { icon: 'ri-user-line', text: 'Для всех покупателей, включая новых' },
    { icon: 'ri-coupon-line', text: 'Промокод VIT40 вводится при оформлении заказа' },
    { icon: 'ri-calendar-line', text: 'Акция не суммируется с другими скидками' },
    { icon: 'ri-truck-line', text: 'Доставка оплачивается отдельно по тарифам' },
    { icon: 'ri-store-2-line', text: 'Действует как в онлайн-заказе, так и в аптеке' },
    { icon: 'ri-information-line', text: 'Организатор вправе изменить условия без предупреждения' },
  ],
  2: [
    { icon: 'ri-heart-3-line', text: 'Кардиомагнил, Аспаркам, Венарус, Троксевазин и другие' },
    { icon: 'ri-shopping-bag-line', text: 'Минимальная сумма заказа — 800 ₽' },
    { icon: 'ri-coupon-line', text: 'Промокод HEART30 вводится при оформлении' },
    { icon: 'ri-calendar-line', text: 'Акция действует до 15 апреля включительно' },
    { icon: 'ri-information-line', text: 'Не распространяется на рецептурные препараты' },
    { icon: 'ri-user-line', text: 'Для всех зарегистрированных пользователей' },
    { icon: 'ri-store-2-line', text: 'Действует только при онлайн-заказе' },
  ],
  3: [
    { icon: 'ri-calendar-2-line', text: 'Скидка действует каждую субботу и воскресенье' },
    { icon: 'ri-price-tag-3-line', text: 'Применяется автоматически, промокод не нужен' },
    { icon: 'ri-store-2-line', text: 'Распространяется на весь каталог без исключений' },
    { icon: 'ri-time-line', text: 'Заказ должен быть оформлен в выходной день' },
    { icon: 'ri-information-line', text: 'Не суммируется с другими акциями и промокодами' },
    { icon: 'ri-user-line', text: 'Для всех покупателей без ограничений' },
    { icon: 'ri-truck-line', text: 'Скидка не распространяется на стоимость доставки' },
  ],
};

interface PromoConditionsProps {
  offer: PromoOffer;
}

const PromoConditions = ({ offer }: PromoConditionsProps) => {
  const { ref, isVisible } = useScrollReveal({ threshold: 0.1 });
  const conditions = CONDITIONS[offer.id] ?? [];

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className="px-6 lg:px-12 py-12"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'opacity 0.6s ease, transform 0.6s ease',
      }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Conditions list */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <div
                className="w-8 h-8 flex items-center justify-center rounded-lg"
                style={{ background: `${offer.color}15`, border: `1px solid ${offer.color}30` }}
              >
                <i className="ri-file-list-3-line text-sm" style={{ color: offer.color }} />
              </div>
              <h2 className="font-orbitron font-bold text-xl text-white">Условия акции</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {conditions.map((c, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 p-4 rounded-xl"
                  style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
                >
                  <div
                    className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0 mt-0.5"
                    style={{ background: `${offer.color}12`, border: `1px solid ${offer.color}25` }}
                  >
                    <i className={`${c.icon} text-sm`} style={{ color: offer.color }} />
                  </div>
                  <span className="font-mono text-xs text-white/55 leading-relaxed">{c.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Side info card */}
          <div className="flex flex-col gap-4">
            {/* Promo period */}
            <div
              className="p-5 rounded-2xl"
              style={{ background: `${offer.color}08`, border: `1px solid ${offer.color}20` }}
            >
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-calendar-event-line" style={{ color: offer.color }} />
                <span className="font-orbitron font-bold text-sm text-white">Период акции</span>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="font-mono text-xs text-white/40">Начало:</span>
                  <span className="font-mono text-xs text-white/70">1 апреля 2026</span>
                </div>
                <div
                  className="h-px"
                  style={{ background: `${offer.color}20` }}
                />
                <div className="flex justify-between items-center">
                  <span className="font-mono text-xs text-white/40">Конец:</span>
                  <span className="font-mono text-xs font-bold" style={{ color: offer.color }}>
                    {new Date(offer.endsAt).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}
                  </span>
                </div>
              </div>
            </div>

            {/* Discount info */}
            <div
              className="p-5 rounded-2xl"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
            >
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-percent-line" style={{ color: offer.colorSecondary }} />
                <span className="font-orbitron font-bold text-sm text-white">Размер скидки</span>
              </div>
              <div
                className="text-center py-4 rounded-xl"
                style={{ background: `${offer.color}10`, border: `1px solid ${offer.color}25` }}
              >
                <div
                  className="font-orbitron font-black text-5xl"
                  style={{ color: offer.color, textShadow: `0 0 20px ${offer.color}60` }}
                >
                  {offer.discount}
                </div>
                <div className="font-mono text-xs text-white/35 mt-1">от обычной цены</div>
              </div>
            </div>

            {/* Promo code */}
            {offer.code && (
              <div
                className="p-5 rounded-2xl"
                style={{ background: 'rgba(255,255,255,0.02)', border: `1px dashed ${offer.color}35` }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-coupon-3-line" style={{ color: offer.color }} />
                  <span className="font-orbitron font-bold text-sm text-white">Промокод</span>
                </div>
                <div
                  className="text-center py-3 rounded-xl font-orbitron font-black text-2xl tracking-widest"
                  style={{ background: `${offer.color}10`, color: offer.color, letterSpacing: '0.2em' }}
                >
                  {offer.code}
                </div>
                <p className="font-mono text-[10px] text-white/30 text-center mt-2">
                  Введите при оформлении заказа
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PromoConditions;
